import * as React from "react";
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { API_ADMIN_BASE_URL } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Command, CommandGroup, CommandItem, CommandList,
} from "@/components/ui/command";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious,
} from "@/components/ui/pagination";
import { cn } from "@/lib/utils";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Search, ChevronsUpDown, Check, X, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

interface UserSubscription {
  plan: string;
  status: string;
  monthlyCredits: number;
  creditsUsed: number;
  maxCanvases: number;
  currentPeriodEnd?: string;
}

interface User {
  id: string;
  email: string;
  name: string;
  image?: string;
  role: string;
  emailVerified: boolean;
  banned: boolean;
  banReason?: string;
  twoFactorEnabled: boolean;
  createdAt: string;
  updatedAt: string;
  subscription?: UserSubscription | null;
  _count: {
    canvases: number;
    jobs: number;
    invites: number;
  };
}

interface UserStats {
  totalUsers: number;
  verifiedUsers: number;
  unverifiedUsers: number;
  usersThisMonth: number;
  usersThisWeek: number;
  bannedUsers: number;
  adminUsers: number;
  planDistribution: Record<string, number>;
}

function getInitials(name: string): string {
  return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function getPlanBadgeVariant(plan: string): "default" | "secondary" | "outline" {
  switch (plan) {
    case "ENTERPRISE": return "default";
    case "PRO": return "secondary";
    default: return "outline";
  }
}

const usersSearchSchema = z.object({
  search: z.string().optional(),
  role: z.string().optional(),
  verified: z.string().optional(),
  banned: z.string().optional(),
  plan: z.string().optional(),
  page: z.coerce.number().optional(),
})

export const Route = createFileRoute('/dashboard/users/')({
  validateSearch: usersSearchSchema,
  component: UsersPage,
})

function UsersPage() {
  const navigate = useNavigate();
  const searchParams = Route.useSearch();
  const [users, setUsers] = React.useState<User[]>([]);
  const [stats, setStats] = React.useState<UserStats | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [total, setTotal] = React.useState(0);

  // Filters
  const [search, setSearch] = React.useState(searchParams.search || "");
  const [selectedRole, setSelectedRole] = React.useState<string>(searchParams.role || "all");
  const [verifiedFilter, setVerifiedFilter] = React.useState<string>(searchParams.verified || "all");
  const [bannedFilter, setBannedFilter] = React.useState<string>(searchParams.banned || "all");
  const [planFilter, setPlanFilter] = React.useState<string>(searchParams.plan || "all");

  // Combobox open states
  const [roleOpen, setRoleOpen] = React.useState(false);
  const [verifiedOpen, setVerifiedOpen] = React.useState(false);
  const [bannedOpen, setBannedOpen] = React.useState(false);
  const [planOpen, setPlanOpen] = React.useState(false);

  // Pagination
  const [page, setPage] = React.useState(searchParams.page || 1);
  const limit = 20;

  // Create user dialog
  const [showCreateDialog, setShowCreateDialog] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const [newEmail, setNewEmail] = React.useState("");
  const [newName, setNewName] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [newRole, setNewRole] = React.useState("user");

  // Delete user dialog
  const [deleteTarget, setDeleteTarget] = React.useState<User | null>(null);
  const [deleting, setDeleting] = React.useState(false);

  // Update URL with current filters
  const updateURL = React.useCallback(() => {
    navigate({
      to: '.',
      search: (prev) => ({
        ...prev,
        search: search || undefined,
        role: selectedRole !== "all" ? selectedRole : undefined,
        verified: verifiedFilter !== "all" ? verifiedFilter : undefined,
        banned: bannedFilter !== "all" ? bannedFilter : undefined,
        plan: planFilter !== "all" ? planFilter : undefined,
        page: page !== 1 ? page : undefined,
      }),
      replace: true,
    });
  }, [search, selectedRole, verifiedFilter, bannedFilter, planFilter, page, navigate]);

  const fetchUsers = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", limit.toString());
      params.set("offset", ((page - 1) * limit).toString());
      if (search) params.set("search", search);
      if (selectedRole !== "all") params.set("role", selectedRole);
      if (verifiedFilter !== "all") params.set("verified", verifiedFilter);
      if (bannedFilter !== "all") params.set("banned", bannedFilter);
      if (planFilter !== "all") params.set("plan", planFilter);

      const response = await fetch(`${API_ADMIN_BASE_URL}/api/admin/users?${params}`, { credentials: "include" });
      if (!response.ok) throw new Error(`Failed to fetch users: ${response.status}`);
      const data = await response.json();
      setUsers(data.data.users);
      setTotal(data.data.total);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  }, [search, selectedRole, verifiedFilter, bannedFilter, planFilter, page, limit]);

  const fetchStats = React.useCallback(async () => {
    try {
      const response = await fetch(`${API_ADMIN_BASE_URL}/api/admin/users/stats`, { credentials: "include" });
      if (!response.ok) return;
      const data = await response.json();
      setStats(data.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, []);

  const handleCreateUser = async () => {
    if (!newEmail || !newPassword || !newName) return;
    setCreating(true);
    try {
      const response = await fetch(`${API_ADMIN_BASE_URL}/api/admin/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: newEmail, name: newName, password: newPassword, role: newRole }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => null);
        throw new Error(err?.error || `Failed to create user: ${response.status}`);
      }
      toast.success("User created successfully!");
      setShowCreateDialog(false);
      setNewEmail(""); setNewName(""); setNewPassword(""); setNewRole("user");
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to create user.");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const response = await fetch(`${API_ADMIN_BASE_URL}/api/admin/users/${deleteTarget.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to delete user");
      toast.success("User deleted successfully.");
      setDeleteTarget(null);
      fetchUsers();
      fetchStats();
    } catch {
      toast.error("Failed to delete user.");
    } finally {
      setDeleting(false);
    }
  };

  React.useEffect(() => { updateURL(); }, [search, selectedRole, verifiedFilter, bannedFilter, planFilter, page, updateURL]);
  React.useEffect(() => { fetchUsers(); }, [fetchUsers]);
  React.useEffect(() => { fetchStats(); }, [fetchStats]);
  React.useEffect(() => { document.title = "Users - Admin"; }, []);
  React.useEffect(() => { setPage(1); }, [search, selectedRole, verifiedFilter, bannedFilter, planFilter]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="bg-background h-full overflow-auto">
      <header>
        <div className="border-border flex h-12 shrink-0 items-center gap-2 border-b px-4 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12 md:h-16 md:px-6">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem><BreadcrumbLink href="/dashboard">Dashboard</BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem><BreadcrumbPage>Users</BreadcrumbPage></BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <div className="border-border flex flex-col border-b px-4 py-4 md:px-6 md:py-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold tracking-tight md:text-3xl">Users</h1>
              <p className="text-muted-foreground text-sm">
                {total.toLocaleString()} users
                {stats && (<span className="ml-2">· {stats.usersThisWeek} this week · {stats.bannedUsers} banned</span>)}
              </p>
            </div>
            <Button size="sm" onClick={() => setShowCreateDialog(true)}><Plus className="mr-1.5 h-3.5 w-3.5" />Create User</Button>
          </div>
          {stats && (
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-6">
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Total</span><span className="text-lg font-semibold">{stats.totalUsers.toLocaleString()}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Verified</span><span className="text-lg font-semibold">{stats.verifiedUsers.toLocaleString()}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">This Month</span><span className="text-lg font-semibold">{stats.usersThisMonth.toLocaleString()}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">This Week</span><span className="text-lg font-semibold">{stats.usersThisWeek.toLocaleString()}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Admins</span><span className="text-lg font-semibold">{stats.adminUsers.toLocaleString()}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Banned</span><span className="text-lg font-semibold">{stats.bannedUsers.toLocaleString()}</span></div>
            </div>
          )}
        </div>
      </header>

      <main className="flex-1 space-y-6 px-4 py-6 md:px-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full md:w-[300px]">
            <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
            <Input placeholder="Search by name or email..." className="pl-8" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="flex gap-3 flex-wrap items-center justify-between w-full">
            <div className="flex gap-3 flex-wrap">
              <Popover open={roleOpen} onOpenChange={setRoleOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-[130px] justify-between">
                    {selectedRole === "all" ? "All Roles" : selectedRole}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[150px] p-0">
                  <Command><CommandList><CommandGroup>
                    {["all", "user", "admin", "viewer"].map((role) => (
                      <CommandItem key={role} value={role} onSelect={() => { setSelectedRole(role); setRoleOpen(false); }}>
                        <Check className={cn("mr-2 h-4 w-4", selectedRole === role ? "opacity-100" : "opacity-0")} />
                        {role === "all" ? "All Roles" : role.charAt(0).toUpperCase() + role.slice(1)}
                      </CommandItem>
                    ))}
                  </CommandGroup></CommandList></Command>
                </PopoverContent>
              </Popover>
              <Popover open={verifiedOpen} onOpenChange={setVerifiedOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-[130px] justify-between">
                    {verifiedFilter === "all" ? "All" : verifiedFilter === "true" ? "Verified" : "Unverified"}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[150px] p-0">
                  <Command><CommandList><CommandGroup>
                    {[{ value: "all", label: "All" }, { value: "true", label: "Verified" }, { value: "false", label: "Unverified" }].map((opt) => (
                      <CommandItem key={opt.value} value={opt.value} onSelect={() => { setVerifiedFilter(opt.value); setVerifiedOpen(false); }}>
                        <Check className={cn("mr-2 h-4 w-4", verifiedFilter === opt.value ? "opacity-100" : "opacity-0")} />
                        {opt.label}
                      </CommandItem>
                    ))}
                  </CommandGroup></CommandList></Command>
                </PopoverContent>
              </Popover>
              <Popover open={bannedOpen} onOpenChange={setBannedOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-[130px] justify-between">
                    {bannedFilter === "all" ? "All" : bannedFilter === "true" ? "Banned" : "Active"}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[150px] p-0">
                  <Command><CommandList><CommandGroup>
                    {[{ value: "all", label: "All" }, { value: "true", label: "Banned" }, { value: "false", label: "Active" }].map((opt) => (
                      <CommandItem key={opt.value} value={opt.value} onSelect={() => { setBannedFilter(opt.value); setBannedOpen(false); }}>
                        <Check className={cn("mr-2 h-4 w-4", bannedFilter === opt.value ? "opacity-100" : "opacity-0")} />
                        {opt.label}
                      </CommandItem>
                    ))}
                  </CommandGroup></CommandList></Command>
                </PopoverContent>
              </Popover>
              <Popover open={planOpen} onOpenChange={setPlanOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-[140px] justify-between">
                    {planFilter === "all" ? "All Plans" : planFilter}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[150px] p-0">
                  <Command><CommandList><CommandGroup>
                    {["all", "FREE", "PRO", "ENTERPRISE"].map((plan) => (
                      <CommandItem key={plan} value={plan} onSelect={() => { setPlanFilter(plan); setPlanOpen(false); }}>
                        <Check className={cn("mr-2 h-4 w-4", planFilter === plan ? "opacity-100" : "opacity-0")} />
                        {plan === "all" ? "All Plans" : plan}
                      </CommandItem>
                    ))}
                  </CommandGroup></CommandList></Command>
                </PopoverContent>
              </Popover>
            </div>
            <Button variant="ghost" size="icon" onClick={() => { setSearch(""); setSelectedRole("all"); setVerifiedFilter("all"); setBannedFilter("all"); setPlanFilter("all"); }} className="h-9 w-9" title="Clear all filters">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="relative overflow-hidden rounded-lg border">
          <div className="overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[280px]">User</TableHead>
                  <TableHead className="w-[80px]">Role</TableHead>
                  <TableHead className="w-[80px]">Plan</TableHead>
                  <TableHead className="w-[80px]">Status</TableHead>
                  <TableHead className="w-[80px] text-center">Canvases</TableHead>
                  <TableHead className="w-[80px] text-center">Jobs</TableHead>
                  <TableHead className="w-[120px]">Joined</TableHead>
                  <TableHead className="w-[50px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Loading users...</TableCell></TableRow>
                ) : users.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No users found</TableCell></TableRow>
                ) : users.map((user) => (
                  <TableRow key={user.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate({ to: `/dashboard/users/${user.id}` })}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8"><AvatarImage src={user.image || undefined} alt={user.name} /><AvatarFallback className="text-xs">{getInitials(user.name || user.email)}</AvatarFallback></Avatar>
                        <div className="min-w-0"><p className="font-medium truncate">{user.name}</p><p className="text-muted-foreground text-xs truncate">{user.email}</p></div>
                      </div>
                    </TableCell>
                    <TableCell><Badge variant={user.role === "admin" ? "default" : user.role === "viewer" ? "secondary" : "outline"} className="text-xs">{user.role}</Badge></TableCell>
                    <TableCell>{user.subscription ? (<Badge variant={getPlanBadgeVariant(user.subscription.plan)} className="text-xs">{user.subscription.plan}</Badge>) : (<span className="text-muted-foreground text-xs">-</span>)}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {user.banned ? (<Badge variant="destructive" className="text-xs">Banned</Badge>) : !user.emailVerified ? (<Badge variant="outline" className="text-xs text-amber-600">Unverified</Badge>) : (<Badge variant="outline" className="text-xs text-green-600">Active</Badge>)}
                      </div>
                    </TableCell>
                    <TableCell className="text-center"><span className="text-sm">{user._count.canvases}</span></TableCell>
                    <TableCell className="text-center"><span className="text-sm">{user._count.jobs}</span></TableCell>
                    <TableCell><span className="text-muted-foreground text-xs">{formatDate(user.createdAt)}</span></TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteTarget(user); }}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {totalPages > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem><PaginationPrevious onClick={() => setPage(Math.max(1, page - 1))} className={page === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"} /></PaginationItem>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) pageNum = i + 1;
                else if (page <= 3) pageNum = i + 1;
                else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = page - 2 + i;
                return (<PaginationItem key={pageNum}><PaginationLink onClick={() => setPage(pageNum)} isActive={page === pageNum} className="cursor-pointer">{pageNum}</PaginationLink></PaginationItem>);
              })}
              <PaginationItem><PaginationNext onClick={() => setPage(Math.min(totalPages, page + 1))} className={page === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"} /></PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </main>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => { if (!open) { setNewEmail(""); setNewName(""); setNewPassword(""); setNewRole("user"); } setShowCreateDialog(open); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create User</DialogTitle>
            <DialogDescription>Create a new user account with email and password.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="create-name">Name</Label>
              <Input id="create-name" placeholder="Full name" value={newName} onChange={(e) => setNewName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-email">Email</Label>
              <Input id="create-email" type="email" placeholder="user@example.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-password">Password</Label>
              <Input id="create-password" type="password" placeholder="Minimum 8 characters" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-role">Role</Label>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} disabled={creating}>Cancel</Button>
            <Button onClick={handleCreateUser} disabled={creating || !newEmail || !newPassword || !newName}>{creating ? "Creating..." : "Create User"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete user?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{deleteTarget?.name}</strong> ({deleteTarget?.email}) and all associated data. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUser} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {deleting ? "Deleting..." : "Delete User"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
